import { gql, useMutation } from '@apollo/client'
import { Mutation_Root } from 'shared/presenters/graphqlTypes'
import useBasket from './useBasket'
import { definedNotNull } from 'shared/presenters/presenter'
import { useRouter } from 'next/router'
import { fbPixelTrack } from 'shared/helpers/pageTrackerTools'
import storageApi from 'helpers/storageApi'

type AddToBasketResponseKeys =
  | 'Consumer_Baskets_AddActivityBooking'
  | 'Consumer_Baskets_AddCreditVoucher'
  | 'Consumer_Baskets_AddOptionalExtra'
  | 'Consumer_Baskets_AddPurchasedClassPass'

type AddToBasketResponseTypes = Pick<Mutation_Root, AddToBasketResponseKeys>

export type UseAddToBasketModalsIds = 'addToBasket' | 'error'

export interface AddToBasketInterface {
  variables: Record<string, unknown>
  onSuccess?: () => void
  onError?: () => void
}

type AddToBasket = (props: AddToBasketInterface) => Promise<void>

/**
 * Add items to the basket allowing the use of different mutations. See  AddToBasketResponseKeys for reference of possible responses.
 */
const useAddToBasket = ({
  chargeable
}: {
  chargeable: keyof typeof ADD_TO_BASKET
}) => {
  const { basket, refetchBasket } = useBasket()
  const { query } = useRouter()
  const widgetSlug = query.widgetSlug as string
  const [
    addMutation,
    { loading: addToBasketLoading, error: addToBasketError }
  ] = useMutation<AddToBasketResponseTypes>(ADD_TO_BASKET[chargeable], {
    onCompleted(response) {
      refetchBasket({ basketId: getBasketIdFromResponse(response) })
    }
  })

  const addToBasket: AddToBasket = async ({
    variables,
    onSuccess,
    onError
  }) => {
    await addToBasketFunc(
      variables,
      determineIdForFetch(basket?.id, variables?.basketId),
      widgetSlug,
      chargeable,
      addMutation,
      onSuccess,
      onError
    )
  }

  return {
    addToBasket,
    addToBasketLoading,
    addToBasketError
  }
}

export default useAddToBasket

/**
 * The `useMutation` in this file is used by multiple mutations, which means the response
 * is dynamic as well. This function is a helper to find the basket id whatever the response is.
 *
 * The response will always be { [only one key from AddToBasketResponseKeys]: Basket_Response }
 */
const getBasketIdFromResponse = (response: AddToBasketResponseTypes) => {
  return definedNotNull(Object.values(response)[0]?.basket_id)
}

/**
 * We facilitate passing in the basketId manually for cases where React may not have
 * updated state yet
 * This is happening in particular for the ModalContentRemoveMembership
 */
const determineIdForFetch = (
  stateBasketId: unknown,
  variablesBasketId: unknown
) => {
  return typeof variablesBasketId !== 'undefined'
    ? variablesBasketId
    : (stateBasketId ?? null)
}

export const addToBasketFunc = async (
  variables: Record<string, unknown>,
  basketIdToUse: unknown,
  widgetSlug: string,
  chargeable: string,
  addMutation: (variables: AddToBasketInterface['variables']) => void,
  onSuccess: (() => void) | undefined,
  onError: (() => void) | undefined
) => {
  /**
   * We charge centers a higher fee if the enthusiast has been linked to the schedulable from the eola marketplace
   * We check to see if this is the case and indicate to the backend by passing an empty string as the widgetSlug
   */
  const marketplaceReferal = storageApi.getMarketplaceReferral({ widgetSlug })

  const widgetSlugToUse =
    marketplaceReferal && (chargeable === 'activity' || chargeable === 'rental')
      ? ''
      : widgetSlug

  try {
    await addMutation({
      variables: {
        ...variables,
        basketId: basketIdToUse,
        widgetSlug: widgetSlugToUse,
        marketplaceSlug: marketplaceReferal
      }
    })

    onSuccess && onSuccess()

    fbPixelTrack({ event: 'AddToCart' })
  } catch (err) {
    onError && onError()
  }
}

const ADD_ACTIVITY_TO_BASKET = gql`
  mutation WidgetUseAddToBasketAddActivityToBasket(
    $basketId: ID
    $schedulableId: ID!
    $timeSlotGroupId: ID
    $adultsGoing: Int!
    $childrenGoing: Int!
    $organizerParticipating: Boolean!
    $exclusive: Boolean
    $isGift: Boolean
    $referralCode: String
    $code: String
    $activityVoucherId: ID
    $optionalExtrasAndQuantities: [chosen_optional_extra_input!]
    $widgetSlug: String!
    $marketplaceSlug: String
  ) {
    Consumer_Baskets_AddActivityBooking(
      basket_id: $basketId
      schedulable_id: $schedulableId
      time_slot_group_id: $timeSlotGroupId
      adults_going: $adultsGoing
      children_going: $childrenGoing
      organizer_participating: $organizerParticipating
      exclusive: $exclusive
      is_gift: $isGift
      referral_code: $referralCode
      code: $code
      activity_voucher_id: $activityVoucherId
      optional_extras_and_quantities: $optionalExtrasAndQuantities
      widget_slug: $widgetSlug
      marketplace_slug: $marketplaceSlug
    ) {
      basket_id
      basket {
        id
        line_items {
          id
        }
      }
      price_after_adjustments_pence
    }
  }
`

const ADD_RENTAL_TO_BASKET = gql`
  mutation WidgetUseAddToBasketAddRentalToBasket(
    $basketId: ID
    $schedulableId: ID!
    $start: String!
    $finish: String!
    $adultsGoing: Int!
    $childrenGoing: Int!
    $organizerParticipating: Boolean!
    $referralCode: String
    $code: String
    $enthusiastName: String
    $enthusiastEmail: String
    $optionalExtrasAndQuantities: [chosen_optional_extra_input!]
    $resourceRequirementQuantities: [resource_requirement_quantity_input!]!
    $widgetSlug: String!
    $marketplaceSlug: String
  ) {
    Consumer_Baskets_AddRentalBooking(
      basket_id: $basketId
      schedulable_id: $schedulableId
      start: $start
      finish: $finish
      adults_going: $adultsGoing
      children_going: $childrenGoing
      organizer_participating: $organizerParticipating
      referral_code: $referralCode
      code: $code
      enthusiast_name: $enthusiastName
      enthusiast_email: $enthusiastEmail
      optional_extras_and_quantities: $optionalExtrasAndQuantities
      resource_requirement_quantities: $resourceRequirementQuantities
      widget_slug: $widgetSlug
      marketplace_slug: $marketplaceSlug
    ) {
      basket_id
      basket {
        id
      }
      price_after_adjustments_pence
    }
  }
`

const ADD_CLASS_PASS_TO_BASKET = gql`
  mutation WidgetUseAddToBasketAddClassPassToBasket(
    $basketId: ID
    $classPassId: ID!
    $enthusiastName: String
    $enthusiastEmail: String
    $widgetSlug: String!
    $marketplaceSlug: String
  ) {
    Consumer_Baskets_AddPurchasedClassPass(
      basket_id: $basketId
      class_pass_id: $classPassId
      enthusiast_name: $enthusiastName
      enthusiast_email: $enthusiastEmail
      widget_slug: $widgetSlug
      marketplace_slug: $marketplaceSlug
    ) {
      basket_id
      basket {
        id
      }
      price_after_adjustments_pence
    }
  }
`

const ADD_CREDIT_TO_BASKET = gql`
  mutation WidgetUseAddToBasketAddCreditToBasket(
    $basketId: ID
    $outletId: ID!
    $amount: Int!
    $widgetSlug: String!
    $marketplaceSlug: String
  ) {
    Consumer_Baskets_AddCreditVoucher(
      basket_id: $basketId
      outlet_id: $outletId
      amount: $amount
      widget_slug: $widgetSlug
      marketplace_slug: $marketplaceSlug
    ) {
      basket_id
      basket {
        id
      }
      price_after_adjustments_pence
    }
  }
`

const ADD_MEMBERSHIP_TO_BASKET = gql`
  mutation WidgetUseAddToBasketAddMembershipToBasket(
    $priceId: ID!
    $basketId: ID
    $widgetSlug: String!
    $marketplaceSlug: String
  ) {
    Consumer_Baskets_AddMembershipSubscription(
      price_id: $priceId
      basket_id: $basketId
      widget_slug: $widgetSlug
    ) {
      basket_id
      basket {
        id
        line_items {
          id
        }
      }
      price_after_adjustments_pence
    }
  }
`

export const ADD_TO_BASKET = {
  activity: ADD_ACTIVITY_TO_BASKET,
  rental: ADD_RENTAL_TO_BASKET,
  classPass: ADD_CLASS_PASS_TO_BASKET,
  credit: ADD_CREDIT_TO_BASKET,
  membership: ADD_MEMBERSHIP_TO_BASKET
}
